import PartnershipIcon from 'components/Common/Vendors/Partnership/PartnershipIcon'
import Tooltip from 'LuxKit/Tooltip'
import GeoContext from 'contexts/geoContext'
import React, { useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import IconLabel from './IconLabel'
import Label from './Label'

type Variant = 'default' | 'prominent' | 'icon' | 'icon-prominent'
type LabelType = 'use-only' | 'earn-and-use' | 'earn-points' | 'earn-only'
interface Config extends Omit<React.ComponentProps<typeof Label>, 'children' | 'style'> {
  style?: (partnership: App.Partnership) => React.CSSProperties
}

const variantConfig: Record<Variant, Config> = {
  default: {
    kind: 'plain',
    variant: 'dark',
    startIcon: <PartnershipIcon />,
  },
  icon: {
    kind: 'secondary',
    variant: 'ghost',
  },
  'icon-prominent': {
    kind: 'primary',
    variant: 'ghost',
    style: (partnership) => ({
      backgroundColor: partnership.color,
    }),
  },
  prominent: {
    kind: 'primary',
    variant: 'dark',
    startIcon: <PartnershipIcon />,
    style: (partnership) => ({
      backgroundColor: partnership.color,
    }),
  },
}

interface Props {
  /**
   * @default default
   */
  variant?: Variant
  /**
   * @default true (I have no idea why)
   */
  canEarn?: boolean
  /**
   * @default true (I have no idea why)
   */
  canUse?: boolean
  /**
   * The number of points the user will earn
   */
  points?: number
  className?: string
}

/**
 * _**NOTE**: Partnership label relies on the value of the `partnership` attribute in the Geo state._
 */
function PartnershipLabel(props: Props) {
  const { variant = 'default', canEarn = true, canUse = true, className, points } = props
  const { partnership } = useContext(GeoContext)
  const intl = useIntl()

  const labelType = useMemo<LabelType | undefined>(() => {
    if (partnership) {
      const hasEarn = canEarn && partnership.hasEarn
      const hasUse = canUse && partnership.hasBurn
      if (points) {
        return 'earn-points'
      } else if (hasEarn && hasUse) {
        return 'earn-and-use'
      } else if (hasEarn) {
        return 'earn-only'
      } else if (hasUse) {
        return 'use-only'
      }
    }
  }, [canEarn, canUse, partnership, points])

  const config = useMemo<Config | undefined>(() => {
    if (!partnership) return undefined
    return variantConfig[variant]
  }, [partnership, variant])

  const labelContent = useMemo(() => {
    if (!partnership || !labelType) return undefined
    const i18nLabelMap: Record<LabelType, string> = {
      'use-only': intl.formatMessage(
        {
          id: 'FVDKLk3ga5',
          defaultMessage: 'Use {rewardName}',
          description: 'Partnership label for using reward points',
        },
        { rewardName: partnership.rewardName },
      ),
      'earn-and-use': intl.formatMessage(
        {
          id: 'SYC8uS8v/B',
          defaultMessage: 'Earn & Use {rewardName}',
          description: 'Partnership label for earning and using reward points',
        },
        { rewardName: partnership.rewardName },
      ),
      'earn-points': intl.formatMessage(
        {
          id: '6ed5fXkirp',
          defaultMessage: 'Earn {formattedPoints} {rewardName}',
          description: 'Partnership label showing specific points earned with reward program name',
        },
        {
          formattedPoints: points ? Math.floor(points).toLocaleString() : 0,
          rewardName: partnership.rewardName,
        },
      ),
      'earn-only': intl.formatMessage(
        {
          id: '/C9Je3GW7I',
          defaultMessage: 'Earn {rewardName}',
          description: 'Partnership label for earning reward points',
        },
        { rewardName: partnership.rewardName },
      ),
    }
    return i18nLabelMap[labelType]
  }, [intl, partnership, labelType, points])

  if (!config || !labelType || !partnership || !labelContent) {
    return null
  }

  if (variant === 'icon' || variant === 'icon-prominent') {
    return <Tooltip
      className={className}
      placement="right"
      description={labelContent}
    >
      <IconLabel
        {...config}
        style={config.style?.(partnership)}
        icon={<PartnershipIcon />}
      />
    </Tooltip>
  }

  return <Label
    {...config}
    style={config.style?.(partnership)}
    className={className}
    data-testid="partnership-label"
  >
    {labelContent}
  </Label>
}

export default PartnershipLabel
